import { FC, useEffect, useState } from 'react'
import questionsService from '../../services/questionsService'
import { IQuestionV2 } from '../../store/reports/types'
import PageBodyLayout from '../PageBodyLayout'
import CardTAG from '../CardTAG'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../store'
import {
  postNewCase,
  setNewCaseAnswers,
  resetAllFields,
} from '../../store/questions/questionsSlice'
import SingleSelectQuestion from './SingleSelectQuestion'
import OpenQuestion from './OpenQuestion'
import MultipleSelectQuestion from './MultipleSelectQuestion'
import LogicalQuestion from './LogicalQuestion'
import NumberQuestion from './NumberQuestion'
import DateQuestion from './DateQuestion'
import ReporterFormQuestion from './ReporterFormQuestion'
import VictimFormQuestion from './VictimFormQuestion'
import { useNavigate } from 'react-router-dom'
import FormQuestion from './FormQuestion'
import SubjectFormQuestion from './SubjectFormQuestion'
import SubmitCancelButtons from '../SubmitCancelButtons'
import { v4 as uuidv4 } from 'uuid'

import { error, success } from '../../utils/alertUtils';

/* eslint-disable */
export function transformStructure(structure: IQuestionV2, subOptionId?: number) {
  // Initialize the transformed structure
  let transformed: {
    answer: { question: number; text: string }
    options: number[]
    subanswers: any
    uuid: string
    option?: number
  } = {
    answer: {
      question: structure?.id,
      text: '',
    },
    options: [],
    subanswers: [],
    uuid: structure?.uuid,
    // uuid: `${Math.random()}`,
    ...(subOptionId ? { option: subOptionId } : {}),
  }

  // If the structure has subquestions, transform them recursively
  if (structure?.subquestions.length > 0) {
    transformed.subanswers = structure?.subquestions.map(subquestion => {
      // const optionId = structure?.options[subquestion?.option - 1]?.id
      const optionId = subquestion.option
      return transformStructure(subquestion, optionId)
    })
  }

  return transformed
}

const questionTypes: { [key: string]: any } = {
  SINGLE_SELECT_VERTICAL: (
    question: IQuestionV2,
    isParent: boolean,
    victimQuestionId?: number | null,
  ) => <SingleSelectQuestion question={question} victimQuestionId={victimQuestionId} />,
  SINGLE_SELECT_HORIZONTAL: (
    question: IQuestionV2,
    isParent: boolean,
    victimQuestionId?: number | null,
  ) => <SingleSelectQuestion question={question} victimQuestionId={victimQuestionId} />,
  OPEN: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <OpenQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  CHECKBOX: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <MultipleSelectQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  MULTIPLE_SELECT: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <MultipleSelectQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  LOGICAL_VERTICAL: (
    question: IQuestionV2,
    isParent: boolean,
    victimQuestionId?: number | null,
  ) => (
    <LogicalQuestion question={question} victimQuestionId={victimQuestionId} isParent={isParent} />
  ),
  LOGICAL_HORIZONTAL: (
    question: IQuestionV2,
    isParent: boolean,
    victimQuestionId?: number | null,
  ) => (
    <LogicalQuestion question={question} victimQuestionId={victimQuestionId} isParent={isParent} />
  ),
  NUMBER: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <NumberQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  DATE: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <DateQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  FORM_REPORTER: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <ReporterFormQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  FORM_VICTIM: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <VictimFormQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  FORM: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <FormQuestion question={question} victimQuestionId={victimQuestionId} />
  ),
  FORM_SUBJECT: (question: IQuestionV2, isParent: boolean, victimQuestionId?: number | null) => (
    <SubjectFormQuestion question={question} />
  ),
}

export const questionParser = (
  question: IQuestionV2,
  isParent?: boolean,
  victimQuestionId?: number | null,
) => {
  const questionVariantType = question?.variant?.type
  if (questionVariantType in questionTypes) {
    return questionTypes[questionVariantType](question, isParent ?? false, victimQuestionId)
  }
  return <></>
}

const NewCase: FC = () => {
  const [questions, setQuestions] = useState<IQuestionV2[]>([])

  const dispatch = useDispatch<AppDispatch>()
  const newAnswers = useSelector(state => state)
  const navigate = useNavigate()

  const changeSubquestionUUID = (question: any) => {
    if (question?.uuid) {
      question.uuid = uuidv4()
    }

    if (question?.subquestions && question?.subquestions?.length > 0) {
      question.subquestions = question?.subquestions.map((item: any) => {
        /* eslint-disable-next-line */
        item.uuid = uuidv4()
        return changeSubquestionUUID(item)
      })
    }
    return question
  }

  const getQuestions = async () => {
    const { data } = await questionsService.getNewCaseQuestions()
   
    const questionsWithNewUuids = data.map((item: any) => {
      return changeSubquestionUUID(item)
    })
    const transformedStructures = questionsWithNewUuids.map(transformStructure)
    setQuestions(questionsWithNewUuids)
    dispatch(setNewCaseAnswers({ answers: transformedStructures }))
  }

  useEffect(() => {
    getQuestions()
  }, [])

  const renderQuestions = () => {
    return questions?.map((question: IQuestionV2) => {
       return <CardTAG title={question?.text_third}>{questionParser(question, true)}</CardTAG>
    })
  }

  const submitHandler = () => {
    dispatch(postNewCase())
      .then(() => {
        success('New case created successfully')
        navigate('/cases/inbox')
      })
      .catch((err: Error) => {
        error('Error creating new case')
        // console.error('Error creating new case', err);
      })

    dispatch(resetAllFields())
  }

  const cancelHandler = () => {
    navigate('/cases/inbox')
    dispatch(resetAllFields())
  }

  return (
    <PageBodyLayout
      className='t-new-case-page'
      dataPart={
        <>
          {questions.length > 0 && renderQuestions()}
          <SubmitCancelButtons
            submitHandler={submitHandler}
            cancelHandler={cancelHandler}
            submitButtonText='Submit'
            cancelButtonText='Cancel'
          />
        </>
      }
      filterPart={undefined}
    />
  )
}

export default NewCase
